// OWNER: AREA-OPS (dashboard, applications/*, suppressions, audit, system). Components go in src/features/ops.
// Webhook endpoints: CRUD, enable/pause, signing secret, test send, and deliveries.
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { History, MoreHorizontal, Pencil, Plus, Trash2, Webhook } from 'lucide-react'
import { useCallback, useMemo, useState } from 'react'
import type { WebhookEndpoint } from '@/api/types'
import { Can } from '@/auth/Can'
import { usePermissions } from '@/auth/usePermissions'
import { ConfirmDialog } from '@/components/ConfirmDialog'
import { DataTable, tableColumns } from '@/components/DataTable'
import { EmptyState } from '@/components/EmptyState'
import { RelativeTime } from '@/components/RelativeTime'
import { Badge } from '@/components/ui/badge'
import { Button, IconButton } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Switch } from '@/components/ui/switch'
import { Tooltip } from '@/components/ui/tooltip'
import { useDeleteWebhook, useUpdateWebhook, webhooksQuery } from '@/features/ops/api'
import { WebhookDialog } from '@/features/ops/WebhookDialog'
import { WebhookSheet } from '@/features/ops/WebhookSheet'

export const Route = createFileRoute('/applications/$appId/webhooks')({
  staticData: { crumb: 'Webhooks' },
  /** `webhook` opens that endpoint's drawer, so the view is linkable. */
  validateSearch: (s: Record<string, unknown>): { webhook?: string } => ({
    webhook: typeof s.webhook === 'string' && s.webhook ? s.webhook : undefined,
  }),
  component: AppWebhooksPage,
})

interface Handlers {
  open: (w: WebhookEndpoint) => void
  edit: (w: WebhookEndpoint) => void
  remove: (w: WebhookEndpoint) => void
  toggle: (w: WebhookEndpoint, enabled: boolean) => void
}

const col = tableColumns<WebhookEndpoint>()

function buildColumns(h: Handlers, canManage: boolean) {
  return col.columns([
    col.accessor('url', {
      header: 'Endpoint',
      cell: (c) => (
        <div className="min-w-0">
          <div className="truncate font-mono text-[13px] text-ink">{c.getValue()}</div>
          {c.row.original.description && (
            <div className="truncate text-xs text-ink-3">{c.row.original.description}</div>
          )}
        </div>
      ),
      enableHiding: false,
    }),
    col.accessor('events', {
      header: 'Events',
      cell: (c) => {
        const ev = c.getValue()
        if (ev.length === 0) return <span className="text-ink-2">All events</span>
        const shown = ev.slice(0, 2)
        return (
          <Tooltip content={ev.join(', ')}>
            <span className="inline-flex flex-wrap gap-1">
              {shown.map((e) => (
                <Badge key={e} tone="outline" className="font-mono">
                  {e.replace('message.', '')}
                </Badge>
              ))}
              {ev.length > shown.length && (
                <Badge tone="outline">+{ev.length - shown.length}</Badge>
              )}
            </span>
          </Tooltip>
        )
      },
      enableSorting: false,
    }),
    col.accessor('enabled', {
      header: 'Enabled',
      cell: (c) =>
        canManage ? (
          <Switch
            aria-label={c.getValue() ? 'Pause webhook' : 'Enable webhook'}
            checked={c.getValue()}
            onCheckedChange={(v) => h.toggle(c.row.original, v)}
          />
        ) : c.getValue() ? (
          <Badge tone="success">Enabled</Badge>
        ) : (
          <Badge>Paused</Badge>
        ),
      meta: { width: '6.5rem', stopRowClick: true },
    }),
    col.accessor('updated_at', {
      header: 'Updated',
      cell: (c) => <RelativeTime value={c.getValue()} className="text-ink-2" />,
      sortFn: 'datetime',
      meta: { align: 'right', width: '8rem' },
    }),
    col.display({
      id: 'actions',
      header: () => <span className="sr-only">Actions</span>,
      cell: (c) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <IconButton label="Webhook actions" size="sm">
              <MoreHorizontal />
            </IconButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onSelect={() => h.open(c.row.original)}>
              <History /> Secret, test, and deliveries
            </DropdownMenuItem>
            {canManage && (
              <>
                <DropdownMenuItem onSelect={() => h.edit(c.row.original)}>
                  <Pencil /> Edit
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem destructive onSelect={() => h.remove(c.row.original)}>
                  <Trash2 /> Delete
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      ),
      enableHiding: false,
      meta: { align: 'right', width: '3.5rem', stopRowClick: true, label: '' },
    }),
  ])
}

function AppWebhooksPage() {
  const { appId } = Route.useParams()
  const { webhook: openId } = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const { can } = usePermissions()
  const list = useQuery(webhooksQuery(appId))
  const [adding, setAdding] = useState(false)
  const [editing, setEditing] = useState<WebhookEndpoint | undefined>()
  const [deleting, setDeleting] = useState<WebhookEndpoint | undefined>()
  const del = useDeleteWebhook()
  const toggle = useUpdateWebhook()
  const canManage = can('manage_config')

  const setOpenId = useCallback(
    (id: string | undefined) =>
      void navigate({ search: (prev) => ({ ...prev, webhook: id }), replace: !id }),
    [navigate],
  )
  const setEnabled = toggle.mutate
  const columns = useMemo(
    () =>
      buildColumns(
        {
          open: (w) => setOpenId(w.id),
          edit: setEditing,
          remove: setDeleting,
          toggle: (w, enabled) => setEnabled({ id: w.id, body: { enabled } }),
        },
        canManage,
      ),
    [setOpenId, setEnabled, canManage],
  )
  const selected = list.data?.find((w) => w.id === openId)

  return (
    <>
      <DataTable
        aria-label="Webhooks"
        columns={columns}
        data={list.data ?? []}
        getRowId={(w) => w.id}
        isLoading={list.isPending}
        error={list.error}
        onRetry={() => void list.refetch()}
        onRowClick={(w) => setOpenId(w.id)}
        isRowActive={(w) => w.id === openId}
        toolbar={
          <>
            <p className="text-sm text-ink-2">
              Signed POSTs to your service when this application’s messages change status.
            </p>
            <div className="ml-auto">
              <Can permission="manage_config" mode="disable">
                <Button variant="primary" icon={<Plus />} onClick={() => setAdding(true)}>
                  Add webhook
                </Button>
              </Can>
            </div>
          </>
        }
        empty={
          <EmptyState
            icon={Webhook}
            title="No webhooks yet"
            description="Add an endpoint to learn when messages are delivered, bounce, or fail, without polling the API."
            action={
              canManage ? (
                <Button variant="primary" icon={<Plus />} onClick={() => setAdding(true)}>
                  Add webhook
                </Button>
              ) : undefined
            }
          />
        }
      />
      <WebhookDialog
        appId={appId}
        open={adding}
        onOpenChange={setAdding}
        onCreated={(w) => setOpenId(w.id)}
      />
      <WebhookDialog
        appId={appId}
        webhook={editing}
        open={!!editing}
        onOpenChange={(o) => !o && setEditing(undefined)}
      />
      <WebhookSheet
        webhook={selected}
        open={!!selected}
        onOpenChange={(o) => !o && setOpenId(undefined)}
      />
      <ConfirmDialog
        open={!!deleting}
        onOpenChange={(o) => !o && setDeleting(undefined)}
        title="Delete this webhook?"
        description={
          <>
            MailSmith stops posting to <span className="font-mono break-all">{deleting?.url}</span>{' '}
            and deletes its delivery history.
          </>
        }
        confirmLabel="Delete webhook"
        destructive
        onConfirm={async () => {
          if (!deleting) return
          await del.mutateAsync(deleting.id)
          if (openId === deleting.id) setOpenId(undefined)
        }}
      />
    </>
  )
}
